import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import React, { useMemo } from "react";
import { isNumeric } from "utils/utils";
import SparkLineHandler from "./SparkLineHandler";
import { AdvancedProps, SparkLineProps } from "./SparkLineTypes";

export default function SparkLine(props: SparkLineProps) {
  const valueScaling = props.valueScaling || 1;
  const count = props.count || 14;
  const dataset = isNumeric(props.dataset)
    ? `${props.dataset}:CATEGORIZED_DATASET`
    : (props.dataset as string);

  const { data, isLoading } = useGetRecentData([dataset], {
    count,
    refTime: props.referenceTime,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: !!props.dataset
    },
  });
  const transformedData = useMemo(() => {
    if (!data || data.length === 0) return { dataArray: [], lastValueTime: null };

    const ob = data[0].data.sort((a, b) => a.key.localeCompare(b.key));
    let tempDataArr: number[] = [];

    if (props.showCurrent && ob.length > 1) {
      const currentTime = new Date().getTime();
      const diffBetweenTwoDatePoints =
        new Date(ob[1].key).getTime() - new Date(ob[0].key).getTime();
      const lastGoodValueTime = new Date(ob[ob.length - 1].key).getTime();
      const pointsBetween = Math.floor(
        (currentTime - lastGoodValueTime) / diffBetweenTwoDatePoints
      );
      const overlappingDates = pointsBetween <= count;
      const pointsDifference = count - pointsBetween;

      if (!overlappingDates) {
        tempDataArr = ob.map((item: InstantDoubleValue) => item.value);
      } else if (ob.length < count) {
        tempDataArr.length = ob.length;
        tempDataArr.fill(0);
        for (let i = 0; i < ob.length; i++) {
          tempDataArr[i] = ob[i].value;
        }
      } else {
        tempDataArr.length = count;
        tempDataArr.fill(0);
        for (let i = 0; i < pointsDifference; i++) {
          tempDataArr[i] = ob[i]?.value;
        }
      }
    } else {
      tempDataArr = ob.map((val: InstantDoubleValue) => val.value);
    }

    if (valueScaling !== 1) {
      tempDataArr = tempDataArr.map((val: number) => val * valueScaling);
    }

    const lastValueTime = ob.length
      ? new Date(ob.sort((a, b) => b.key.localeCompare(a.key))[0].key as string)
      : null;

    return { dataArray: tempDataArr, lastValueTime };
  }, [data, props.showCurrent, valueScaling, count]);

  if (isLoading) {
    return <i className="pi pi-spin pi-spinner" style={{ fontSize: "4em" }} />;
  }

  const advancedProps: AdvancedProps = {
    baselineDataset: props.baselineDataset,
    baselineWidth: props.baselineWidth,
    baselineColor: props.baselineColor,
    fillAboveColor: props.fillAboveColor,
    fillBelowColor: props.fillBelowColor,
    count,
    useConstant: props.useConstant,
    constant: props.constant,
    updateFrequency: props.updateFrequency,
    referenceTime: props.referenceTime,
  }

  return data && data.length !== 0 ? (
    <SparkLineHandler
      dataArray={transformedData.dataArray}
      lastValueTime={transformedData.lastValueTime}
      decimalPlaces={props.decimalPlaces}
      labelFontSize={props.labelFontSize}
      sparklineWidth={props.sparkLineWidth}
      lineColor={props.lineColor}
      lineWidth={props.lineWidth}
      unit={props.unit}
      showMinMax={props.showMinMax}
      dataset={dataset}
      title={data[0].title}
      height={props.height}
      overlayLabel={props.overlayLabel}
      vertical={props.vertical}
      horizontal={props.horizontal}
      advanced={props.advanced}
      advancedProps={advancedProps}
    />
  ) : (
    <div style={{ height: `${props.height?? "auto"}px` }}>
      <i className="pi pi-spin pi-spinner" style={{ fontSize: "4em" }} />
    </div>
  );
}
